import type { KaleidoscopeSettings } from "../types/kaleidoscope";
import { normalizeKaleidoscopeSourceImageIndex } from "./kaleidoscope-sources";

export const DEFAULT_KALEIDOSCOPE_SETTINGS: KaleidoscopeSettings = {
  colors: ["#f7c95e", "#d28dff", "#79c6ff", "#3f76ff", "#1c3158", "#090d18"],
  sourceImageIndex: 0,
  segments: 10,
  facetDensity: 0.74,
  patternComplexity: 0.72,
  centerStructure: 0.7,
  outerReflection: 0.68,
  layerDepth: 0.76,
  rotationSpeed: 0.18,
  morphSpeed: 0.28,
  shimmerSpeed: 0.42,
  pulseStrength: 0.24,
  driftAmount: 0.2,
  glowStrength: 0.8,
  bloomRadius: 0.34,
  highlightIntensity: 0.82,
  refractionStrength: 0.26,
  contrast: 0.68,
  sparkleDensity: 0.26,
  microFacetStrength: 0.58,
  textureAmount: 0.54,
  paletteIntensity: 1.02,
  colorSeparation: 0.68,
  darkLightBalance: 0.46,
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function round(value: number, digits = 2) {
  return Number(value.toFixed(digits));
}

function randomBetween(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function hslToHex(h: number, s: number, l: number) {
  const saturation = s / 100;
  const lightness = l / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const hue = h / 60;
  const x = chroma * (1 - Math.abs((hue % 2) - 1));
  let red = 0;
  let green = 0;
  let blue = 0;

  if (hue >= 0 && hue < 1) {
    red = chroma;
    green = x;
  } else if (hue < 2) {
    red = x;
    green = chroma;
  } else if (hue < 3) {
    green = chroma;
    blue = x;
  } else if (hue < 4) {
    green = x;
    blue = chroma;
  } else if (hue < 5) {
    red = x;
    blue = chroma;
  } else {
    red = chroma;
    blue = x;
  }

  const match = lightness - chroma / 2;
  const toHex = (channel: number) =>
    Math.round((channel + match) * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
}

function createCuratedPalette() {
  const baseHue = Math.random() * 360;
  const arc = randomBetween(34, 110);
  const direction = Math.random() > 0.5 ? 1 : -1;
  const saturation = randomBetween(48, 84);
  const darkLight = randomBetween(10, 18);
  const midLight = randomBetween(40, 56);
  const brightLight = randomBetween(70, 86);
  const palette = Array.from({ length: 6 }, (_, index) => {
    const step = index / 5;
    const hue = (baseHue + direction * (step - 0.5) * arc + randomBetween(-8, 8) + 360) % 360;
    const sat = clamp(saturation + randomBetween(-10, 8), 38, 90);
    const light =
      index === 5
        ? darkLight
        : index === 0
          ? brightLight
          : midLight + Math.sin(step * Math.PI) * randomBetween(6, 18) + randomBetween(-4, 4);
    return hslToHex(hue, sat, clamp(light, 8, 88));
  });

  return Array.from(new Set(palette)).slice(0, 6).concat(DEFAULT_KALEIDOSCOPE_SETTINGS.colors).slice(0, 6);
}

function isHexColor(value: unknown): value is string {
  return typeof value === "string" && /^#([0-9a-fA-F]{6})$/.test(value);
}

export function normalizeKaleidoscopeSettings(
  input?: Partial<KaleidoscopeSettings> | null
): KaleidoscopeSettings {
  const merged = {
    ...DEFAULT_KALEIDOSCOPE_SETTINGS,
    ...(input ?? {}),
  } as KaleidoscopeSettings;

  return {
    colors:
      Array.isArray(merged.colors) &&
        merged.colors.length >= 6 &&
        merged.colors.slice(0, 6).every(isHexColor)
        ? merged.colors.slice(0, 6)
        : DEFAULT_KALEIDOSCOPE_SETTINGS.colors,
    sourceImageIndex: normalizeKaleidoscopeSourceImageIndex(merged.sourceImageIndex),
    segments: Math.round(clamp(merged.segments, 6, 18) / 2) * 2,
    facetDensity: round(clamp(merged.facetDensity, 0.2, 1.2)),
    patternComplexity: round(clamp(merged.patternComplexity, 0.2, 1.2)),
    centerStructure: round(clamp(merged.centerStructure, 0, 1.2)),
    outerReflection: round(clamp(merged.outerReflection, 0, 1.2)),
    layerDepth: round(clamp(merged.layerDepth, 0.1, 1.1)),
    rotationSpeed: round(clamp(merged.rotationSpeed, 0, 1.2)),
    morphSpeed: round(clamp(merged.morphSpeed, 0, 1.1)),
    shimmerSpeed: round(clamp(merged.shimmerSpeed, 0, 1.2)),
    pulseStrength: round(clamp(merged.pulseStrength, 0, 0.9)),
    driftAmount: round(clamp(merged.driftAmount, 0, 1.1)),
    glowStrength: round(clamp(merged.glowStrength, 0.2, 1.4)),
    bloomRadius: round(clamp(merged.bloomRadius, 0.08, 0.7)),
    highlightIntensity: round(clamp(merged.highlightIntensity, 0, 1.2)),
    refractionStrength: round(clamp(merged.refractionStrength, 0, 0.8)),
    contrast: round(clamp(merged.contrast, 0.2, 1.2)),
    sparkleDensity: round(clamp(merged.sparkleDensity, 0, 1)),
    microFacetStrength: round(clamp(merged.microFacetStrength, 0, 1.2)),
    textureAmount: round(clamp(merged.textureAmount, 0, 1.2)),
    paletteIntensity: round(clamp(merged.paletteIntensity, 0.35, 1.4)),
    colorSeparation: round(clamp(merged.colorSeparation, 0, 1.2)),
    darkLightBalance: round(clamp(merged.darkLightBalance, 0, 1.2)),
  };
}

export function randomizeKaleidoscopeSettings(): KaleidoscopeSettings {
  const serenity = randomBetween(0.4, 0.84);
  const crystalline = randomBetween(0.48, 0.98);
  const richness = randomBetween(0.54, 0.98);
  const brilliance = randomBetween(0.46, 0.94);

  return normalizeKaleidoscopeSettings({
    colors: createCuratedPalette(),
    sourceImageIndex: Math.floor(Math.random() * 5),
    segments: [6, 8, 10, 12, 14, 16][Math.floor(Math.random() * 6)],
    facetDensity: randomBetween(0.42, 1.02) * crystalline,
    patternComplexity: randomBetween(0.48, 1.0) * richness,
    centerStructure: randomBetween(0.34, 0.98) * crystalline,
    outerReflection: randomBetween(0.34, 1.02) * crystalline,
    layerDepth: randomBetween(0.42, 0.98),
    rotationSpeed: randomBetween(0.04, 0.42),
    morphSpeed: randomBetween(0.08, 0.56),
    shimmerSpeed: randomBetween(0.12, 0.84),
    pulseStrength: randomBetween(0.08, 0.42),
    driftAmount: randomBetween(0.02, 0.32) * serenity,
    glowStrength: randomBetween(0.42, 1.02) * brilliance,
    bloomRadius: randomBetween(0.14, 0.46),
    highlightIntensity: randomBetween(0.34, 1.02) * brilliance,
    refractionStrength: randomBetween(0.08, 0.42) * serenity,
    contrast: randomBetween(0.54, 1.02),
    sparkleDensity: randomBetween(0.08, 0.42),
    microFacetStrength: randomBetween(0.34, 0.98) * crystalline,
    textureAmount: randomBetween(0.26, 0.92) * richness,
    paletteIntensity: randomBetween(0.88, 1.18),
    colorSeparation: randomBetween(0.34, 1.02),
    darkLightBalance: randomBetween(0.28, 0.82),
  });
}
